import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {GeoLocationDialogsFactory} from "./geo-location-dialogs-factory";
import {GeoValidationResult, IGeoRunResult} from "../strategies/geo-location.strategy.interface";
import {
    GeoRunnerAction,
    GeoRunnerName,
    IGeoLocationRunner,
    IGeoRetryOnOpenRealGame,
    ILoginGeoLocationOptions,
    IOpenGameGeoLocationOptions,
    IPeriodicGeoUpdateOptions
} from "./geo-location-runner.interface";
import {IGeoRestrictionsUpdateEventParams} from "../../sdk/sdk-events";
import {GeoAnalyticsContext, GeoLocationAnalytics} from "../geo-location-analytics";
import {LocalStorageKeys} from "../../storage/local-storage-keys";
import {formatErrorForLogging} from "../geo-helpers";

export abstract class GeoLocationRunnerBase implements IGeoLocationRunner{
    constructor(protected readonly services: IServiceFactoryExtended, analyticsContext: GeoAnalyticsContext) {
        this._analytics = new GeoLocationAnalytics(analyticsContext, this._getRunnerName(), services);
        this._geoDialogs = new GeoLocationDialogsFactory(services, this._analytics);

    }

    private _runGeoPromise: Promise<IGeoRunResult>;
    protected readonly _geoDialogs: GeoLocationDialogsFactory;
    protected readonly _analytics: GeoLocationAnalytics;

    protected abstract _getRunnerName(): GeoRunnerName;

    abstract geoKick(params: IGeoRestrictionsUpdateEventParams): Promise<IGeoRunResult>;
    abstract loginGeoLocation(options: ILoginGeoLocationOptions): Promise<IGeoRunResult>;
    abstract openGameGeoLocation(options: IOpenGameGeoLocationOptions): Promise<IGeoRunResult>;
    abstract runPeriodicGeo(options: IPeriodicGeoUpdateOptions): Promise<IGeoRunResult>;
    abstract showGameRestrictedPopup(geoResult?: IGeoRunResult | null): void;
    abstract geoRetryOnOpenRealGame(options: IGeoRetryOnOpenRealGame): Promise<IGeoRunResult>;
    abstract showGenericGeoKickMessage(): Promise<void>;

    public waitForGeo(): Promise<IGeoRunResult> {
        if(!this._runGeoPromise) {
            return Promise.reject(`${this._getRunnerName()}.waitForGeo called before performGeoLocation`);
        }

        return this._runGeoPromise;
    }


    protected async _executeGeoFlow(actionName: GeoRunnerAction, geoAction: () => Promise<IGeoRunResult>): Promise<IGeoRunResult> {
        if(this._runGeoPromise) {
            return Promise.reject(`Cannot execute ${this._getRunnerName()}.${actionName} because another geo is already in progress`);
        }

        this._runGeoPromise = geoAction().then(result => {
            this._analytics.reportGeoResult(result);

            return result;
        }).catch(err => {
            this._analytics.reportGeoFailure(err);
            return {
                validationResult: GeoValidationResult.Fail
            }
        });

        return this._runGeoPromise;
    }

    private get _storedClientIpAddresses(): string[] {
        return this.services.localStorage.getJson(LocalStorageKeys.clientIpAddresses) ?? [];
    }

    private _saveClientIpAddress(value: string): void {

        if(!value) {
            return;
        }

        const storedIpAddresses = this._storedClientIpAddresses;
        const index = storedIpAddresses.findIndex(ip => ip === value);
        if(index >= 0) {
            storedIpAddresses.splice(index, 1);
        }
        storedIpAddresses.push(value);

        this.services.localStorage.setJson(LocalStorageKeys.clientIpAddresses, storedIpAddresses);
    }

    async runClientIpDetection(): Promise<void> {
        try {

            const previousIpAddresses = this._storedClientIpAddresses;
            const response = await this.services.api.locationController.detectUserIp({
                PreviousIpAddresses: previousIpAddresses
            });

            if(previousIpAddresses.length > 0 && !previousIpAddresses.includes(response.CurrentIpAddress)) {
                this._analytics.reportIpChangeDetection(previousIpAddresses[previousIpAddresses.length - 1], response.CurrentIpAddress);
            }

            this._saveClientIpAddress(response.CurrentIpAddress);

        } catch (err) {
            this._analytics.reportIpDetectionFailure(formatErrorForLogging(err));
        }
    }
}